import { Component, EventEmitter, Input, Output } from '@angular/core';
import { actionCredits } from 'src/app/services/operators/vesselfinder-credit-actions';
import { vesselFinderSpendBlocker } from '../../vf-credits.model';

/** One rendered history row — a normalised view over a VesselFinder `vesselHistory` record. */
export interface VfHistoryRowView {
  /** mmsi, or imo when no mmsi is known — same identifier `key` the backend stores rows under. */
  key: string;
  mmsi: string | null;
  imo: string | null;
  label: string;
  isNamed: boolean;
  /** "12 min ago" — age of the stored fetch, or null without one. */
  ago: string | null;
  /** Fetch older than 48 h — captioned "last known", hollow glyph. */
  stale: boolean;
  destination: string | null;
  hasFix: boolean;
  /** The raw vesselHistory record — what the outputs emit. */
  data: any;
}

/**
 * VesselFinder "Watched Vessels" — same panel, same UI, same expand/collapse
 * and row grammar as Voyage Radar's `VoyageVesselHistoryComponent` (this
 * component's own header names it, and this one is a deliberate sibling of
 * it, not a copy of the on-map toolbar's `VfWatchedVesselsControlComponent`,
 * which reads the SESSION-only `VesselFinderMapController` list — this one
 * reads `sensor.vesselHistory`, the PERSISTED record, exactly like Voyage
 * Radar's sidebar panel reads `sensor.vesselHistory` and not its own toolbar
 * drawer's controller. Voyage Radar has both too; this is not a divergence.
 *
 * Field-shape difference from Voyage Radar's record, not a design choice:
 * `saveVesselFinderVessel` (`eetaskapi/routes/vesselFinder.route.js`) stores
 * `{ key, mmsi, imo, name: <fetchedAt Date>, snapshot: { name, callSign,
 * shipType, flag, destination, latitude, longitude, course, speed, heading } }`
 * — note `record.name` is the FETCH TIMESTAMP, not the vessel's name (that is
 * `record.snapshot.name`). Easy to misread; read `rows` below carefully
 * before touching it.
 *
 * Purely presentational, like its Voyage Radar sibling: no Leaflet, no API.
 */
@Component({
  selector: 'app-vf-vessel-history',
  templateUrl: './vf-vessel-history.component.html',
  styleUrls: ['./vf-vessel-history.component.scss'],
})
export class VfVesselHistoryComponent {
  /** The VesselFinder sensor whose `vesselHistory` this renders. */
  @Input() sensor: any;
  /** Keys (mmsi/imo) currently drawn as located markers — the "on map" tag. */
  @Input() onMapKeys: ((key: string) => boolean) | null = null;
  /** Key of the lookup in flight, or null — spins that row's Refresh. */
  @Input() loadingKey: string | null = null;

  /** Draw/centre this vessel on the map (free). Emits the raw record. */
  @Output() locate = new EventEmitter<any>();
  /** Re-run a fresh lookup for this vessel (paid). Emits the raw record. */
  @Output() refresh = new EventEmitter<any>();
  /** Take this vessel's marker off the map, keeping the row. Emits the raw record. */
  @Output() takeOffMap = new EventEmitter<any>();
  /** Delete this vessel from Watched Vessels for good. Emits the raw record. */
  @Output() remove = new EventEmitter<any>();

  /** Visible by default, like Voyage Radar's own Watched Vessels. */
  expanded = true;

  private _cacheKey = '';
  private _rows: VfHistoryRowView[] = [];

  private static readonly STALE_FIX_MS = 48 * 3600 * 1000;

  get history(): any[] {
    return Array.isArray(this.sensor?.vesselHistory) ? this.sensor.vesselHistory : [];
  }

  /** Live catalogue price for the row's Refresh — an ee-admin re-price sticks. */
  get refreshCost(): number {
    return actionCredits(this.sensor, 'search');
  }

  /** What the row's Refresh must go dead for, or null when it stays live. */
  get blocker(): string | null {
    return vesselFinderSpendBlocker(this.sensor, 'search');
  }

  /** Rows, newest fetch first — memoised the same way as the Voyage Radar sibling. */
  get rows(): VfHistoryRowView[] {
    const history = this.history;
    const cacheKey = history
      .map((r: any) => `${r?.key}@${r?.snapshot?.name ?? ''}@${r?.name ?? ''}@${r?.snapshot?.latitude ?? ''}@${r?.stale ?? ''}`)
      .join('|');
    if (cacheKey === this._cacheKey) return this._rows;
    this._cacheKey = cacheKey;

    const now = Date.now();
    this._rows = history
      .map((rec: any): VfHistoryRowView | null => {
        const key = String(rec?.key ?? '').trim();
        if (!key) return null;
        const snap = rec?.snapshot ?? {};
        const name = snap.name ?? null;
        const t = new Date(rec?.name ?? '').getTime();
        const hasFix =
          snap.latitude != null &&
          snap.longitude != null &&
          Number.isFinite(Number(snap.latitude)) &&
          Number.isFinite(Number(snap.longitude));
        const stale = isNaN(t) || !hasFix || rec?.stale === true || now - t > VfVesselHistoryComponent.STALE_FIX_MS;
        const mmsi = rec?.mmsi ? String(rec.mmsi) : null;
        const imo = rec?.imo ? String(rec.imo) : null;
        const fallbackLabel = mmsi ? `MMSI ${mmsi}` : imo ? `IMO ${imo}` : `MMSI/IMO ${key}`;

        return {
          key,
          mmsi,
          imo,
          label: !stale && name ? name : fallbackLabel,
          isNamed: !stale && !!name,
          ago: this.relAge(t, now),
          stale,
          destination: !stale ? (snap.destination ?? null) : null,
          hasFix,
          data: rec,
        };
      })
      .filter((r): r is VfHistoryRowView => r !== null)
      .reverse(); // stored oldest→newest; the panel reads newest first
    return this._rows;
  }

  private relAge(t: number, now: number): string | null {
    if (isNaN(t)) return null;
    const mins = Math.max(0, Math.round((now - t) / 60000));
    if (mins < 60) return `${mins} min ago`;
    if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
    return `${Math.round(mins / 1440)} d ago`;
  }

  isOnMap(row: VfHistoryRowView): boolean {
    return this.onMapKeys ? this.onMapKeys(row.key) : false;
  }

  /** A stale vessel or a record without a fix cannot be located without refreshing first. */
  canLocate(row: VfHistoryRowView): boolean {
    return !row.stale && (row.hasFix || this.isOnMap(row));
  }

  isLoading(row: VfHistoryRowView): boolean {
    return this.loadingKey === row.key;
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  /** Row click = Locate (draw/centre) — the row's primary act. */
  pick(row: VfHistoryRowView): void {
    if (!this.canLocate(row)) return;
    this.locate.emit(row.data);
  }

  locateRow(row: VfHistoryRowView, event: Event): void {
    event.stopPropagation();
    if (!this.canLocate(row)) return;
    this.locate.emit(row.data);
  }

  refreshRow(row: VfHistoryRowView, event: Event): void {
    event.stopPropagation();
    if (this.loadingKey || this.blocker) return;
    this.refresh.emit(row.data);
  }

  takeOffMapRow(row: VfHistoryRowView, event: Event): void {
    event.stopPropagation();
    this.takeOffMap.emit(row.data);
  }

  removeRow(row: VfHistoryRowView, event: Event): void {
    event.stopPropagation();
    this.remove.emit(row.data);
  }

  trackByKey(_i: number, row: VfHistoryRowView): string {
    return row.key;
  }
}
